if (!customElements.get('image-carousel-component')) {
  class ImageCarouselComponent extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.marqueeState = null;
    }

    getGap() {
      const desktop = parseInt(this.dataset.gap, 10) || 0;
      const mobile = parseInt(this.dataset.gapMobile, 10);
      const mobileGap = Number.isNaN(mobile) ? desktop : mobile;
      return window.matchMedia('(min-width: 750px)').matches ? desktop : mobileGap;
    }

    connectedCallback() {
      const container = this.querySelector('.image-carousel__swiper');
      if (!container) return;

      const autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 0;
      const pauseOnHover = this.dataset.pauseOnHover === 'true';
      const isMarquee = autoplaySpeed > 0;

      if (isMarquee) {
        this.initMarquee(container, autoplaySpeed, pauseOnHover);
        return;
      }

      if (typeof Swiper === 'undefined') return;

      const slideCount = container.querySelectorAll('.swiper-slide').length;

      this.swiper = new Swiper(container, {
        slidesPerView: 'auto',
        spaceBetween: this.getGap(),
        grabCursor: true,
        simulateTouch: true,
        allowTouchMove: true,
        watchOverflow: true,
        loop: slideCount > 1,
        loopedSlides: slideCount,
        loopAdditionalSlides: slideCount,
        freeMode: {
          enabled: true,
          momentum: true,
          momentumRatio: 0.35,
        },
        navigation: {
          nextEl: this.querySelector('.image-carousel__button--next'),
          prevEl: this.querySelector('.image-carousel__button--prev'),
        },
      });

      this.resizeHandler = () => {
        if (!this.swiper) return;
        const newGap = this.getGap();
        if (this.swiper.params.spaceBetween !== newGap) {
          this.swiper.params.spaceBetween = newGap;
          this.swiper.update();
        }
      };
      window.addEventListener('resize', this.resizeHandler);

      if (Shopify.designMode) {
        this.blockSelectHandler = this.onBlockSelect.bind(this);
        document.addEventListener('shopify:block:select', this.blockSelectHandler);
      }
    }

    initMarquee(container, autoplaySpeed, pauseOnHover) {
      const wrapper = container.querySelector('.swiper-wrapper');
      if (!wrapper || wrapper.children.length < 2) return;

      if (Shopify.designMode) return;

      const slides = [...wrapper.children];
      const track = document.createElement('div');
      track.className = 'image-carousel__marquee-track';

      slides.forEach((slide) => {
        slide.classList.remove('swiper-slide');
        track.appendChild(slide);
      });

      slides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.classList.add('image-carousel__slide--clone');
        clone.removeAttribute('id');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => {
          el.removeAttribute('data-shopify-editor-block');
        });
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      });

      container.classList.remove('swiper');
      wrapper.replaceWith(track);

      let offset = 0;
      let isDragging = false;
      let isPaused = false;
      let dragStartX = 0;
      let dragStartOffset = 0;
      let rafId = null;

      const pixelsPerSecond = 120 / autoplaySpeed;

      const getSetWidth = () => track.scrollWidth / 2;

      const normalizeOffset = () => {
        const setWidth = getSetWidth();
        if (setWidth <= 0) return;
        while (offset <= -setWidth) offset += setWidth;
        while (offset > 0) offset -= setWidth;
      };

      const applyTransform = () => {
        track.style.transform = `translate3d(${offset}px, 0, 0)`;
      };

      const tick = (timestamp) => {
        if (!this.marqueeState) return;

        if (!this.marqueeState.lastTimestamp) {
          this.marqueeState.lastTimestamp = timestamp;
        }

        const elapsed = timestamp - this.marqueeState.lastTimestamp;
        this.marqueeState.lastTimestamp = timestamp;

        if (!isDragging && !isPaused) {
          offset -= (pixelsPerSecond * elapsed) / 1000;
          normalizeOffset();
          applyTransform();
        }

        rafId = requestAnimationFrame(tick);
      };

      const onPointerDown = (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        isDragging = true;
        isPaused = true;
        dragStartX = event.clientX;
        dragStartOffset = offset;
        track.setPointerCapture(event.pointerId);
        this.classList.add('image-carousel--dragging');
      };

      const onPointerMove = (event) => {
        if (!isDragging) return;
        offset = dragStartOffset + (event.clientX - dragStartX);
        applyTransform();
      };

      const endDrag = (event) => {
        if (!isDragging) return;
        isDragging = false;
        normalizeOffset();
        applyTransform();
        if (track.hasPointerCapture(event.pointerId)) {
          track.releasePointerCapture(event.pointerId);
        }
        this.classList.remove('image-carousel--dragging');
        isPaused = pauseOnHover && container.matches(':hover');
      };

      track.addEventListener('pointerdown', onPointerDown);
      track.addEventListener('pointermove', onPointerMove);
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);

      const handlers = { onPointerDown, onPointerMove, endDrag };

      if (pauseOnHover) {
        handlers.onMouseEnter = () => {
          if (!isDragging) isPaused = true;
        };
        handlers.onMouseLeave = () => {
          if (!isDragging) isPaused = false;
        };
        container.addEventListener('mouseenter', handlers.onMouseEnter);
        container.addEventListener('mouseleave', handlers.onMouseLeave);
      }

      this.marqueeState = {
        rafId,
        track,
        container,
        handlers,
      };

      rafId = requestAnimationFrame(tick);
      this.marqueeState.rafId = rafId;
    }

    disconnectedCallback() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      if (this.marqueeState) {
        cancelAnimationFrame(this.marqueeState.rafId);
        const { track, container, handlers } = this.marqueeState;
        track.removeEventListener('pointerdown', handlers.onPointerDown);
        track.removeEventListener('pointermove', handlers.onPointerMove);
        track.removeEventListener('pointerup', handlers.endDrag);
        track.removeEventListener('pointercancel', handlers.endDrag);
        if (handlers.onMouseEnter) {
          container.removeEventListener('mouseenter', handlers.onMouseEnter);
          container.removeEventListener('mouseleave', handlers.onMouseLeave);
        }
        this.marqueeState = null;
      }

      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
      }

      if (this.blockSelectHandler) {
        document.removeEventListener('shopify:block:select', this.blockSelectHandler);
      }
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      if (!event.target.classList.contains('image-carousel__slide')) return;
      if (event.target.classList.contains('image-carousel__slide--clone')) return;
      if (!this.swiper) return;

      const slideIndex = parseInt(event.target.dataset.slideIndex, 10);
      if (!isNaN(slideIndex)) {
        this.swiper.slideToLoop(slideIndex);
      }
    }
  }

  customElements.define('image-carousel-component', ImageCarouselComponent);
}
